import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useCallback, useContext } from 'react';
import { Controller, useForm } from 'react-hook-form';

import ProgramSelect from '@/components/ProgramSelect/ProgramSelect';
import AppContext from '@/contexts/appContext';
import FieldError from '@/forms/FieldError';
import type {
  Props,
  StartNewPhaseFormPayload,
  StartNewPhaseFormValues
} from '@/forms/StartNewPhaseForm/types';
import PhaseRepository from '@/repositories/PhaseRepository';
import { Button, InputGroup } from '@/ui';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

const StartNewPhaseForm = ({ episodeOfCare, onCancel, onSuccess, personId }: Props) => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const phaseRepository = PhaseRepository();

  const initialValues: StartNewPhaseFormValues = {
    primary_substance: '',
    program_id: ''
  };

  const {
    formState: { errors },
    control,
    handleSubmit,
    register,
    setValue
  } = useForm({ defaultValues: initialValues });

  const startNewPhase = useMutation({
    mutationFn: ({ values }: { values: StartNewPhaseFormPayload }) => {
      return phaseRepository.create({
        data: { episode_of_care: episodeOfCare.id, ...values }
      });
    },
    onSuccess: () => {
      addMessage({ level: 'success', text: 'Successfully started new phase' });
      personId && queryClient.invalidateQueries({ queryKey: ['getPerson', personId] });
      onSuccess?.();
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const submitForm = (values: StartNewPhaseFormValues) => {
    startNewPhase.mutate({ values });
  };

  const onPrimarySubstanceChange = useCallback(
    (primarySubstance: string | null) => setValue('primary_substance', primarySubstance ?? ''),
    [setValue]
  );

  const onProgramChange = useCallback(
    (program: string | null) => setValue('program_id', program ?? ''),
    [setValue]
  );

  register('primary_substance', {
    required: 'Primary Concern is required'
  });

  return (
    <form onSubmit={handleSubmit(submitForm)} data-testid="form-start-new-phase" className="pt-4">
      <InputGroup>
        <FieldError message={errors.primary_substance?.message} />
        <Controller
          name="program_id"
          control={control}
          render={() => {
            return (
              <ProgramSelect
                onPrimarySubstanceChange={onPrimarySubstanceChange}
                onProgramChange={onProgramChange}
              />
            );
          }}
          rules={{
            required: 'Program is required'
          }}
        />

        <FieldError message={errors.program_id?.message} />
      </InputGroup>

      <InputGroup>
        <div className="flex items-center gap-1 justify-end">
          <Button
            color="primary"
            disabled={startNewPhase.isPending}
            isLoading={startNewPhase.isPending}
            type="submit">
            Transition
          </Button>
          {onCancel && (
            <Button variant="outline" color="neutral" onClick={onCancel} type="button">
              Cancel
            </Button>
          )}
        </div>
      </InputGroup>
    </form>
  );
};

export default StartNewPhaseForm;
